import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Heart, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface MoodEntry {
  id: string;
  user_id: string;
  mood_level: number;
  journal_entry: string | null;
  tags: string[] | null;
  created_at: string;
}

const MOODS = [
  { level: 1, emoji: "😢", label: "Very Low", bgColor: "bg-red-500/10 hover:bg-red-500/20", borderColor: "border-red-500/30 hover:border-red-500", textColor: "text-red-400" },
  { level: 2, emoji: "😟", label: "Low", bgColor: "bg-orange-500/10 hover:bg-orange-500/20", borderColor: "border-orange-500/30 hover:border-orange-500", textColor: "text-orange-400" },
  { level: 3, emoji: "😐", label: "Neutral", bgColor: "bg-yellow-500/10 hover:bg-yellow-500/20", borderColor: "border-yellow-500/30 hover:border-yellow-500", textColor: "text-yellow-400" },
  { level: 4, emoji: "🙂", label: "Good", bgColor: "bg-green-500/10 hover:bg-green-500/20", borderColor: "border-green-500/30 hover:border-green-500", textColor: "text-green-400" },
  { level: 5, emoji: "😄", label: "Great", bgColor: "bg-emerald-500/10 hover:bg-emerald-500/20", borderColor: "border-emerald-500/30 hover:border-emerald-500", textColor: "text-emerald-400" },
];

const MOOD_COLORS_SOLID = [
  "bg-red-500",
  "bg-orange-500", 
  "bg-yellow-500",
  "bg-green-500",
  "bg-emerald-500",
];

const SUGGESTED_TAGS = ["stressed", "relaxed", "anxious", "hopeful", "tired", "energetic", "overwhelmed", "content"];

interface MoodTrackerProps {
  onMoodLogged?: () => void;
}

export const MoodTracker = ({ onMoodLogged }: MoodTrackerProps) => {
  const [entries, setEntries] = useState<MoodEntry[]>([]);
  const [selectedMood, setSelectedMood] = useState<number | null>(null);
  const [journalEntry, setJournalEntry] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const fetchEntries = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("mood_entries")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(7);

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error("Error fetching mood entries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleSubmit = async () => {
    if (!selectedMood) {
      toast({ title: "Please select a mood", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("mood_entries")
        .insert({
          user_id: user.id,
          mood_level: selectedMood,
          journal_entry: journalEntry || null,
          tags: selectedTags.length > 0 ? selectedTags : null,
        });

      if (error) throw error;

      toast({ title: "Mood logged! 💚", description: "Thanks for checking in with yourself." });
      setSelectedMood(null);
      setJournalEntry("");
      setSelectedTags([]);
      fetchEntries();
      onMoodLogged?.();
    } catch (error) {
      console.error("Error saving mood:", error);
      toast({ title: "Error", description: "Failed to save mood entry", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const todayEntry = entries.find(e => 
    format(new Date(e.created_at), "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")
  );

  const getSelectedMoodStyle = (mood: typeof MOODS[0]) => {
    if (selectedMood === mood.level) {
      return `${MOOD_COLORS_SOLID[mood.level - 1]} text-white border-transparent shadow-lg`;
    }
    return `${mood.bgColor} ${mood.borderColor} border-2`;
  };

  return (
    <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
      {/* Gradient accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="pb-3 pt-5">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <div className="p-2 bg-pink/10 rounded-lg">
            <Heart className="h-4 w-4 text-pink" />
          </div>
          How are you feeling today?
        </CardTitle>
        <CardDescription className="text-sm">
          Track your emotional wellness alongside your finances
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {todayEntry ? (
          <div className="p-6 bg-gradient-to-br from-muted/50 to-muted/30 rounded-xl text-center border border-border/50">
            <p className="text-4xl mb-3">{MOODS.find(m => m.level === todayEntry.mood_level)?.emoji}</p>
            <p className="text-sm font-medium text-foreground">You already checked in today</p>
            {todayEntry.journal_entry && (
              <p className="text-sm mt-3 text-muted-foreground italic max-w-md mx-auto">
                "{todayEntry.journal_entry}"
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-5 gap-3">
              {MOODS.map((mood) => (
                <motion.button
                  key={mood.level}
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedMood(mood.level)}
                  className={`
                    p-4 rounded-xl text-center transition-all duration-300
                    ${getSelectedMoodStyle(mood)}
                  `}
                >
                  <span className="text-3xl block mb-2">{mood.emoji}</span>
                  <p className={`text-xs font-semibold ${selectedMood === mood.level ? 'text-white' : mood.textColor}`}>
                    {mood.label}
                  </p>
                </motion.button>
              ))}
            </div>

            <AnimatePresence>
              {selectedMood && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 overflow-hidden"
                >
                  <Textarea
                    placeholder="What's on your mind? (optional)"
                    value={journalEntry}
                    onChange={(e) => setJournalEntry(e.target.value)}
                    className="min-h-[100px] text-sm bg-muted/50 border-border/50 focus:border-primary/50 resize-none"
                  />
                  
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_TAGS.map((tag) => (
                      <Badge
                        key={tag}
                        variant={selectedTags.includes(tag) ? "default" : "outline"}
                        className="cursor-pointer transition-all duration-200 hover:scale-105"
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <Button 
                    onClick={handleSubmit} 
                    disabled={submitting}
                    className="w-full bg-primary hover:bg-primary/90 transition-colors"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {submitting ? "Saving..." : "Log My Mood"}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {entries.length > 0 && (
          <div className="pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-3 font-medium">Recent mood history</p>
            <div className="flex gap-2">
              {entries.slice(0, 7).reverse().map((entry) => (
                <motion.div
                  key={entry.id}
                  whileHover={{ scale: 1.1 }}
                  className={`flex-1 h-10 rounded-lg ${MOOD_COLORS_SOLID[entry.mood_level - 1]} opacity-80 hover:opacity-100 transition-opacity cursor-pointer`}
                  title={`${format(new Date(entry.created_at), "MMM d")}: ${MOODS.find(m => m.level === entry.mood_level)?.label}`}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
